import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Collapse,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Receipt,
  Payment,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Visibility,
  VisibilityOff,
  LocationOn,
  Phone,
} from "@mui/icons-material";
import {
  freightListforInvoiceCreation,
  freightInvoiceCreate,
  freightPrint,
  dueFreightDelete,
  dueFreightUpdate,
  branchpartyList,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

import { T } from "../../../theme";

// ─── Shared Styles (FreightList jaisa) ────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const disabledFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-disabled": { bgcolor: T.white },
    "&.Mui-disabled fieldset": { border: `1.5px solid ${T.border}` },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: T.text,
    fontWeight: 600,
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: T.textSub,
  },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ─── Helpers ─────────────────────────────────────────────────────
const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// ─── ActionButton (FreightList style) ─────────────────────────────
const ActionButton = ({ tip, icon, isDelete, isEdit, isPrint, onClick }) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isPrint
        ? T.success
        : T.primary;
  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isPrint
        ? T.successBg
        : T.primaryBg;
  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isPrint
        ? T.successBorder
        : T.border;
  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isPrint
        ? "#d1fae5"
        : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            width: 20,
            height: 20,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 12 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ─── TruncatedCell ───────────────────────────────────────────────
const TruncatedCell = ({ text, maxWidth = 140 }) => (
  <Tooltip title={text || "—"} arrow>
    <Typography
      sx={{
        fontSize: "0.72rem",
        color: T.text,
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: 500,
      }}
    >
      {text || "—"}
    </Typography>
  </Tooltip>
);

// ─── MobileInvoiceCard (updated colors) ──────────────────────────
const MobileInvoiceCard = ({
  row,
  idx,
  expanded,
  onToggle,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onPrint,
}) => {
  const pkg = row?.package_items?.[0] || {};
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${selected ? T.success : expanded ? T.primary : T.border}`,
        bgcolor: selected ? T.successBg : T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Top Row */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={onSelect}
            sx={{
              p: 0.25,
              color: expanded ? T.white : T.primary,
              "&.Mui-checked": { color: T.success },
            }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.truck_no || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: T.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.from_location || "—"} → {row.to_location || "—"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Net Payable
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.8rem", color: T.primary }}
            >
              ₹{row.net_payable || 0}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                }}
              >
                📋 Basic Info
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>ID:</strong> {row.freight_id}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Loading:</strong> {formatDate(row.loading_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Unloading:</strong> {formatDate(row.unloading_date)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                }}
              >
                ⚖️ Weight
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Load
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    {row.loading_wt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Recv
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    {row.received_wt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Short
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 600, color: T.red }}
                  >
                    {row.shortage_wt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Bill
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    {row.billing_wt || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                }}
              >
                💰 Charges
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Rate:</strong> ₹{row.freight_rate || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>LR:</strong> ₹{row.freight_amt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>TDS:</strong> ₹{row.tds_amt || 0} (
                    {row.tds_per || 0}%)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Shortage:</strong> ₹{row.shortage_amt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Commission:</strong> ₹{row.commission || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Other:</strong> ₹{row.other_exp || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: "#fffbeb",
                border: `1px solid #fde68a`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#d97706",
                  mb: 0.5,
                }}
              >
                💵 Advance
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Mines
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    ₹{row.ad_mines_exp || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Diesel
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    ₹{row.ad_diesel_exp || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
                    Cash
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    ₹{row.ad_cash_exp || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#d97706",
                      mt: 0.25,
                    }}
                  >
                    Total: ₹{row.total_advance || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {(row.truck_owner || row.phone_no || row.pan_no) && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.5,
                  }}
                >
                  👤 Owner
                </Typography>
                {row.truck_owner && (
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Owner:</strong> {row.truck_owner}
                  </Typography>
                )}
                {row.phone_no && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Phone sx={{ fontSize: 11, color: T.primary }} />
                    <Typography sx={{ fontSize: "0.65rem" }}>
                      {row.phone_no}
                    </Typography>
                  </Box>
                )}
                {row.pan_no && (
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>PAN:</strong> {row.pan_no}
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryLight,
                border: `1.5px solid ${T.border}`,
                ...sq,
              }}
            >
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Net Amount
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    ₹{row.net_amount || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Net Payable
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: T.primary,
                    }}
                  >
                    ₹{row.net_payable || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Paid
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: T.success,
                    }}
                  >
                    ₹{row.paid_amount || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Balance
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 600, color: T.red }}
                  >
                    ₹{row.bal_amount || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {row.remark && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Remark:
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: T.text }}>
                  {row.remark}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{ ...successBtn, height: 30, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, row.freight_id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────
export default function FreightInvoice() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentFields, setShowPaymentFields] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [data, setData] = useState([]);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [selectedFreightIds, setSelectedFreightIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [partyOptions, setParties] = useState([]);
  const [rowPayAmounts, setRowPayAmounts] = useState({});

  const [filters, setFilters] = useState({
    party_name: "",
    truck_no: "",
  });
  const [formData, setFormData] = useState({
    freight_id: [],
    total_amount: 0,
    pay_amount: "",
    payment_to: "",
    pay_mode: "",
    upi_check_no: "",
    description: "",
  });

  const [expandedCardId, setExpandedCardId] = useState(null);
  const handleCardToggle = (id) =>
    setExpandedCardId((prev) => (prev === id ? null : id));

  useEffect(() => {
    const fetchParties = async () => {
      const data = await branchpartyList();
      if (data) setParties(data.party);
    };
    fetchParties();
  }, []);

  const fetchFreightList = useCallback(
    async (pageNum, activeFilter = filters, append = false) => {
      setLoading(true);
      try {
        const res = await freightListforInvoiceCreation(pageNum, activeFilter);
        if (res && res.results) {
          if (append) setData((prev) => [...prev, ...res.results]);
          else setData(res.results);
          setHasMore(!!res.next);
          setPage(pageNum);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching LR list:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchFreightList(1, filters, false);
      setExpandedCardId(null);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters, fetchFreightList]);

  const fetchMoreData = async () => {
    if (!hasMore || loading) return;
    await fetchFreightList(page + 1, filters, true);
  };

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleDelete = async (id) => {
    try {
      const del_data = await dueFreightDelete(id);
      setData((prevData) =>
        prevData.filter((row) => row.freight_id !== del_data.freight_id),
      );
      setSnackbar({
        open: true,
        message: "Record deleted successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete record",
        severity: "error",
      });
    }
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };

  const handleEditOpen = (row) => {
    setEditRow({ ...row });
    setEditDialogOpen(true);
  };

  const handleEditChange = (field, value) => {
    const updatedData = { ...editRow, [field]: value };
    const loading =
      parseFloat(field === "loading_wt" ? value : updatedData.loading_wt) || 0;
    const receiving =
      parseFloat(field === "received_wt" ? value : updatedData.received_wt) ||
      0;
    const freightRate =
      parseFloat(field === "freight_rate" ? value : updatedData.freight_rate) ||
      0;
    const tdsRate =
      parseFloat(field === "tds_per" ? value : updatedData.tds_per) || 0;
    const shortageRate =
      parseFloat(
        field === "shortage_rate" ? value : updatedData.shortage_rate,
      ) || 0;
    const commission =
      parseFloat(field === "commission" ? value : updatedData.commission) || 0;
    const minesAdvance =
      parseFloat(field === "ad_mines_exp" ? value : updatedData.ad_mines_exp) ||
      0;
    const dieselAdvance =
      parseFloat(
        field === "ad_diesel_exp" ? value : updatedData.ad_diesel_exp,
      ) || 0;
    const cashAdvance =
      parseFloat(field === "ad_cash_exp" ? value : updatedData.ad_cash_exp) ||
      0;
    const otherExp =
      parseFloat(field === "other_exp" ? value : updatedData.other_exp) || 0;

    updatedData.billing_wt = Math.min(loading, receiving).toFixed(2);
    const shortage = (loading - receiving) * 1000;
    updatedData.shortage_wt = shortage > 0 ? shortage.toFixed(2) : "0";
    const billing = parseFloat(updatedData.billing_wt) || 0;
    updatedData.freight_amt = (freightRate * billing).toFixed(2);
    updatedData.tds_amt = (
      (parseFloat(updatedData.freight_amt) * tdsRate) /
      100
    ).toFixed(2);
    updatedData.shortage_amt = (
      parseFloat(updatedData.shortage_wt) * shortageRate
    ).toFixed(2);
    updatedData.total_advance = (
      minesAdvance +
      dieselAdvance +
      cashAdvance
    ).toFixed(2);
    updatedData.net_amount = (
      parseFloat(updatedData.freight_amt) -
      parseFloat(updatedData.tds_amt) -
      parseFloat(updatedData.shortage_amt) -
      parseFloat(updatedData.total_advance) -
      commission
    ).toFixed(2);
    updatedData.net_payable = (
      parseFloat(updatedData.net_amount) - otherExp
    ).toFixed(2);
    setEditRow(updatedData);
  };

  const handleEditSave = async () => {
    const saveData = { ...editRow };
    saveData.receive_amount = saveData.receive_amount || 0;
    saveData.bal_amount = (
      parseFloat(saveData.net_payable || 0) -
      parseFloat(saveData.paid_amount || 0)
    ).toFixed(2);
    try {
      const res_data = await dueFreightUpdate(saveData.freight_id, saveData);
      setData((prevData) =>
        prevData.map((row) =>
          row.freight_id === res_data.freight_id ? res_data : row,
        ),
      );
      setEditDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Record updated successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update record",
        severity: "error",
      });
    }
  };

  const handlePrintSingleRow = async (row) => {
    try {
      const printData = await freightPrint(row.freight_id);
      const printWindow = window.open("", "", "width=900,height=600");
      const fieldPairs = [
        ["loading_date", "unloading_date"],
        ["from_location", "to_location"],
        ["loading_wt", "received_wt"],
        ["shortage_wt", "billing_wt"],
        ["freight_rate", "freight_amt"],
        ["tds_per", "tds_amt"],
        ["total_advance", "commission"],
        ["shortage_rate", "shortage_amt"],
        ["net_amount", "other_exp"],
        ["net_payable", "truck_no"],
      ];
      row.payments = printData[0]?.payments || [];
      row.freights = printData[0]?.freights || [];
      const tableRows = fieldPairs
        .map(([key1, key2]) => {
          const value1 = row[key1] ?? "";
          const value2 = row[key2] ?? "";
          const formatTitle = (text) =>
            text
              .replace(/_/g, " ")
              .replace(
                /\w\S*/g,
                (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              );
          return `<tr><td style="border:1px solid #000;padding:6px;"><strong>${key1 ? formatTitle(key1) : ""}</strong></td><td style="border:1px solid #000;padding:6px;">${value1}</td><td style="width:2%;"></td><td style="border:1px solid #000;padding:6px;"><strong>${key2 ? formatTitle(key2) : ""}</strong></td><td style="border:1px solid #000;padding:6px;">${value2}</td></tr>`;
        })
        .join("");
      printWindow.document.write(`
        <html><head><title>Print LR</title>
        <style>body{font-family:Arial;padding:20px;}header{text-align:center;font-size:20px;font-weight:bold;margin-bottom:20px;}footer{position:fixed;bottom:10px;width:100%;text-align:center;font-size:12px;}table{border-collapse:collapse;width:100%;}td{font-size:14px;}th{background-color:#ddd;}</style></head>
        <body><header>PARAS ROAD LINES</header>
        <table border="1" style="border-collapse:collapse;" cellpadding="5">
          <tr><td><strong>Ashutosh Ku. Singh</strong></td><td><strong>LR ID: ${row.freight_id}</strong></td><td>${row.truck_owner || ""}</td></tr>
          <tr><td><strong>LR No: ${row.lr_number}</strong></td><td><strong>Net Payable: ${row.net_payable}</strong></td><td>${row.phone_no || ""}</td></tr>
        </table><br/>
        <table border="1" style="border-collapse:collapse;">${tableRows}</table>
        <footer>Powered by: YASHVITECH IT SOLUTION PVT LTD</footer>
        <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}};</script>
        </body></html>
      `);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Error printing:", error);
    }
  };

  const filteredData = data;

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFreightIds([]);
      setRowPayAmounts({});
    } else {
      setSelectedFreightIds(filteredData.map((f) => f.freight_id));
    }
    setSelectAll(!selectAll);
  };

  const handleRowSelect = (freight_id) => {
    const isSelected = selectedFreightIds.includes(freight_id);

    if (isSelected) {
      setRowPayAmounts((p) => {
        const updated = { ...p };
        delete updated[freight_id];
        return updated;
      });
      setSelectedFreightIds((prev) => prev.filter((id) => id !== freight_id));
    } else {
      setSelectedFreightIds((prev) => [...prev, freight_id]);
    }
  };

  useEffect(() => {
    setSelectAll(
      selectedFreightIds.length === filteredData.length &&
        filteredData.length > 0,
    );
  }, [selectedFreightIds, filteredData]);

  const handleRowPayAmountChange = (freightId, value) => {
    setRowPayAmounts((prev) => ({ ...prev, [freightId]: value }));
  };

  const selectedTotalAmount = selectedFreightIds.reduce((acc, id) => {
    const customAmt = rowPayAmounts[id];
    const row = filteredData.find((f) => f.freight_id === id);

    return (
      acc +
      parseFloat(
        customAmt !== undefined && customAmt !== ""
          ? customAmt
          : row?.net_payable || 0,
      )
    );
  }, 0);

  const handleSubmit = async () => {
    if (selectedFreightIds.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one LR",
        severity: "error",
      });
      return;
    }

    const totalAmount = selectedFreightIds.reduce((acc, id) => {
      const customAmt = rowPayAmounts[id];
      const row = filteredData.find((f) => f.freight_id === id);

      return (
        acc +
        parseFloat(
          customAmt !== undefined && customAmt !== ""
            ? customAmt
            : row?.net_payable || 0,
        )
      );
    }, 0);

    const payAmount = parseFloat(formData.pay_amount || 0);
    const remAmount = totalAmount - payAmount;

    const payload = {
      freight_id: selectedFreightIds,
      total_amount: totalAmount,
      paid_amount: payAmount,
      rem_amount: remAmount,
      payments:
        formData.pay_amount && showPaymentFields
          ? [
              {
                pay_amount: formData.pay_amount,
                payment_to: formData.payment_to,
                upi_check_no: formData.upi_check_no,
                pay_mode: formData.pay_mode,
                remark: formData.description,
              },
            ]
          : null,
    };

    try {
      const response = await freightInvoiceCreate(payload);
      if (response) {
        setSnackbar({
          open: true,
          message: "Invoice created successfully",
          severity: "success",
        });
        setFormData({
          freight_id: [],
          total_amount: 0,
          pay_amount: "",
          payment_to: "",
          pay_mode: "",
          upi_check_no: "",
          description: "",
        });
        setSelectedFreightIds([]);
        setSelectAll(false);
        setShowPaymentFields(false);
        fetchFreightList(1, filters, false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to create invoice",
        severity: "error",
      });
    }
  };

  const handlePaymentToggle = () => {
    setShowPaymentFields(!showPaymentFields);
    if (showPaymentFields) {
      setFormData((prev) => ({
        ...prev,
        pay_amount: "",
        payment_to: "",
        pay_mode: "",
        upi_check_no: "",
        description: "",
      }));
    }
  };

  useEffect(() => {
    if (!showPaymentFields) return;

    if (selectedFreightIds.length === 0) {
      setFormData((prev) => ({ ...prev, payment_to: "" }));
      return;
    }
    const selectedRows = selectedFreightIds
      .map((id) => data.find((f) => f.freight_id === id))
      .filter(Boolean);

    const rowWithPaymentTo = selectedRows.find(
      (r) => r.payment_to && r.payment_to.trim() !== "",
    );

    const autoName = rowWithPaymentTo ? rowWithPaymentTo.payment_to.trim() : "";
    setFormData((prev) => {
      if (!prev.payment_to && autoName) {
        return { ...prev, payment_to: autoName };
      }
      return prev;
    });
  }, [selectedFreightIds, showPaymentFields, data]);

  const totals = {
    loading_wt: filteredData
      .reduce((s, c) => s + (+c.loading_wt || 0), 0)
      .toFixed(3),
    received_wt: filteredData
      .reduce((s, c) => s + (+c.received_wt || 0), 0)
      .toFixed(3),
    shortage_wt: filteredData
      .reduce((s, c) => s + (+c.shortage_wt || 0), 0)
      .toFixed(3),
    billing_wt: filteredData
      .reduce((s, c) => s + (+c.billing_wt || 0), 0)
      .toFixed(3),
    freight_amt: filteredData
      .reduce((s, c) => s + (+c.freight_amt || 0), 0)
      .toFixed(1),
    tds_amt: filteredData.reduce((s, c) => s + (+c.tds_amt || 0), 0).toFixed(1),
    shortage_amt: filteredData
      .reduce((s, c) => s + (+c.shortage_amt || 0), 0)
      .toFixed(1),
    total_advance: filteredData
      .reduce((s, c) => s + (+c.total_advance || 0), 0)
      .toFixed(1),
    commission: filteredData
      .reduce((s, c) => s + (+c.commission || 0), 0)
      .toFixed(1),
    net_amount: filteredData
      .reduce((s, c) => s + (+c.net_amount || 0), 0)
      .toFixed(1),
    other_exp: filteredData
      .reduce((s, c) => s + (+c.other_exp || 0), 0)
      .toFixed(1),
    net_payable: filteredData
      .reduce((s, c) => s + (+c.net_payable || 0), 0)
      .toFixed(1),
    paid_amount: filteredData
      .reduce((s, c) => s + (+c.paid_amount || 0), 0)
      .toFixed(1),
    bal_amount: filteredData
      .reduce((s, c) => s + (+c.bal_amount || 0), 0)
      .toFixed(1),
  };

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: -2,
        maxWidth: 1400,
        mx: "auto",
        width: "100%",
        height: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // NO page scroll
      }}
    >
      {/* ── Toolbar ── */}
      <Box sx={{ mt: 2, mb: 1.5, flexShrink: 0 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Party */}
          <Grid item xs={12} sm={4} md={3}>
            <Autocomplete
              size="small"
              options={partyOptions}
              getOptionLabel={(o) => o?.name || ""}
              value={
                partyOptions.find((o) => o.name === filters.party_name) || null
              }
              onChange={(_, v) =>
                handleFilterChange("party_name", v?.name || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Party…"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck (xs: flex-grow se bachi hui puri jagah lega) */}
          <Grid item xs sm={4} md={3}>
            <TextField
              size="small"
              placeholder="Truck…"
              fullWidth
              value={filters.truck_no}
              onChange={(e) => handleFilterChange("truck_no", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalShippingIcon
                      sx={{ fontSize: 14, color: T.primary }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Action Icons (xs="auto": Sirf utni jagah lega jitni buttons ko chahiye) */}
          <Grid item xs="auto" sm="auto" md="auto">
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ height: 34, justifyContent: "flex-end" }}
            >
              <Tooltip title="Clear Filters" arrow>
                <Button
                  sx={{ ...dangerBtn, minWidth: 34, px: 0 }}
                  onClick={() => setFilters({ party_name: "", truck_no: "" })}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Refresh" arrow>
                <Button
                  sx={{ ...iconBtn, minWidth: 34, px: 0 }}
                  onClick={() => fetchFreightList(1, filters, false)}
                >
                  <RefreshIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Count */}
          <Grid item xs={12} sm="auto" md="auto">
            <Box
              sx={{
                height: 34,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                color: T.primary,
                border: `1.5px solid ${T.border}`,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            >
              <Receipt sx={{ fontSize: 15, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {filteredData.length} Records
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Invoice Form Section ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1.5px solid ${T.border}`,
          mb: 1.5,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          flexShrink: 0,
          boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          bgcolor: "transparent",
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField
              label="Total Payable"
              value={formatCurrency(selectedTotalAmount)}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
              sx={disabledFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Selected LR's"
              value={selectedFreightIds.length}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
              sx={disabledFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent={{ xs: "space-between", sm: "flex-end" }}
              alignItems="center"
              width="100%"
            >
              <Button
                onClick={handlePaymentToggle}
                startIcon={
                  showPaymentFields ? (
                    <CloseIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <Payment sx={{ fontSize: 14 }} />
                  )
                }
                size="small"
                sx={{
                  ...ghostBtn,
                  height: 34,
                  px: { xs: 1, sm: 2 },
                  flex: { xs: 1, sm: "initial" }, // Mobile par equal 50% width
                  whiteSpace: "nowrap",
                }}
              >
                {showPaymentFields ? "Cancel Payment" : "Add Payment"}
              </Button>
              <Button
                onClick={handleSubmit}
                startIcon={<Receipt sx={{ fontSize: 14 }} />}
                size="small"
                sx={{
                  ...primaryBtn,
                  height: 34,
                  px: { xs: 1, sm: 2 },
                  flex: { xs: 1, sm: "initial" }, // Mobile par equal 50% width
                  whiteSpace: "nowrap",
                }}
              >
                Create Invoice
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Collapse in={showPaymentFields} sx={{ mt: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                name="pay_amount"
                label="Pay Amount"
                type="number"
                size="small"
                value={formData.pay_amount}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    pay_amount: e.target.value,
                  }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                name="payment_to"
                label="Payment To"
                size="small"
                value={formData.payment_to}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_to: e.target.value,
                  }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                name="pay_mode"
                label="Payment Mode"
                size="small"
                value={formData.pay_mode}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, pay_mode: e.target.value }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                name="upi_check_no"
                label="UPI / Cheque No."
                size="small"
                value={formData.upi_check_no}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    upi_check_no: e.target.value,
                  }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Payment Description"
                size="small"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Paper>
      {/* ── Table / Mobile ── */}
      {isMobile ? (
        <Box
          sx={{
            flex: 1, // ← takes remaining height
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0, // ← CRITICAL for flex children to scroll
          }}
        >
          {/* mobile select-all bar */}
          {filteredData.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                ...sq,
                flexShrink: 0, // ← fixed height
                border: `1.5px solid ${T.border}`,
                bgcolor: T.primaryLight,
                p: 1,
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    sx={{
                      color: T.primary,
                      "&.Mui-checked": { color: T.primary },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    Select All ({selectedFreightIds.length}/
                    {filteredData.length})
                  </Typography>
                }
              />
              {selectedFreightIds.length > 0 && (
                <Chip
                  label={formatCurrency(selectedTotalAmount)}
                  size="small"
                  sx={{
                    ...sq,
                    bgcolor: T.primary,
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                />
              )}
            </Paper>
          )}

          {loading && data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : filteredData.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `1.5px dashed ${T.border}`,
                bgcolor: T.white,
                ...sq,
              }}
            >
              <Receipt sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: T.text,
                  mb: 0.5,
                  fontSize: "0.9rem",
                }}
              >
                No LR records found
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: T.textSub }}>
                Try adjusting your filters
              </Typography>
            </Box>
          ) : (
            <Box
              id="freight-invoice-container-mobile"
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0, // ← CRITICAL
              }}
            >
              <InfiniteScroll
                dataLength={data.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="freight-invoice-container-mobile"
              >
                {filteredData.map((row, idx) => (
                  <MobileInvoiceCard
                    key={row.freight_id}
                    row={row}
                    idx={idx}
                    expanded={expandedCardId === row.freight_id}
                    onToggle={() => handleCardToggle(row.freight_id)}
                    selected={selectedFreightIds.includes(row.freight_id)}
                    onSelect={() => handleRowSelect(row.freight_id)}
                    onEdit={handleEditOpen}
                    onDelete={(e, id) => {
                      setDeleteAnchorEl(e.currentTarget);
                      setDeleteId(id);
                    }}
                    onPrint={handlePrintSingleRow}
                  />
                ))}
              </InfiniteScroll>

              {/* Mobile Summary */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.primaryLight,
                  p: 1.5,
                  mt: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Summary
                </Typography>
                <Grid container spacing={0.75}>
                  {[
                    ["Total LR", formatCurrency(totals.freight_amt), T.text],
                    [
                      "Net Payable",
                      formatCurrency(totals.net_payable),
                      T.primary,
                    ],
                    ["Paid", formatCurrency(totals.paid_amount), T.success],
                    ["Balance", formatCurrency(totals.bal_amount), T.red],
                  ].map(([l, v, c]) => (
                    <Grid item xs={6} key={l}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {l}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                      >
                        {v}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <TableContainer
            id="freight-invoice-container"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="freight-invoice-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1400,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                    height: 28,
                  },
                  "& td": {
                    height: 26,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 40,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        sx={{
                          color: T.primary,
                          "&.Mui-checked": { color: T.primary },
                          p: 0.25,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 40,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 35,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 75,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 80,
                      }}
                    >
                      LR No
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 155,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 85,
                      }}
                    >
                      Truck No
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Party</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>Advance Amt</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>Total Amt</TableCell>
                    <TableCell sx={{ minWidth: 85 }}>Loading</TableCell>
                    <TableCell sx={{ minWidth: 85 }}>Unloading</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>From</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>To</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Owner</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>Phone</TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        top: 0,
                        bgcolor: `${T.headerBg} !important`,
                        zIndex: 13,
                        minWidth: 85,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <Receipt
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = row.is_remember
                        ? "#fff4e6"
                        : isEven
                          ? T.baseRow
                          : T.altRow;
                      const stickyCell = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };

                      return (
                        <TableRow
                          key={row.freight_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...stickyCell,
                              p: "2px 4px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={selectedFreightIds.includes(
                                row.freight_id,
                              )}
                              onChange={() => handleRowSelect(row.freight_id)}
                              sx={{
                                p: 0,
                                color: T.primary,
                                "&.Mui-checked": { color: T.primary },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 40,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>
                          {/* LR No — now sticky at 75 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 75,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {row.lr_number || "—"}
                            </Typography>
                          </TableCell>

                          {/* Truck No — now sticky at 155 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 155,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: T.primary,
                              }}
                            >
                              {row.truck_no}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.party_name}
                              maxWidth={120}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: "#d97706",
                              }}
                            >
                              ₹{row.paid_amount || 0}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: "2px 4px" }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={`₹${row.net_payable}`}
                              disabled={
                                !selectedFreightIds.includes(row.freight_id)
                              }
                              value={
                                selectedFreightIds.includes(row.freight_id)
                                  ? (rowPayAmounts[row.freight_id] ?? "")
                                  : ""
                              }
                              onChange={(e) =>
                                handleRowPayAmountChange(
                                  row.freight_id,
                                  e.target.value,
                                )
                              }
                              inputProps={{
                                style: {
                                  fontSize: "0.72rem",
                                  padding: "3px 6px",
                                  textAlign: "right",
                                  fontWeight: 700,
                                },
                              }}
                              sx={{
                                width: 100,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "1px",
                                  height: 24,
                                  bgcolor: selectedFreightIds.includes(
                                    row.freight_id,
                                  )
                                    ? T.white
                                    : "#f5f5f5",
                                  "& fieldset": {
                                    border: selectedFreightIds.includes(
                                      row.freight_id,
                                    )
                                      ? `1.5px solid ${T.primary}`
                                      : `1.5px solid #bdbdbd`,
                                  },
                                  "&:hover fieldset": {
                                    border: selectedFreightIds.includes(
                                      row.freight_id,
                                    )
                                      ? `1.5px solid ${T.primaryDark}`
                                      : `1.5px solid #9e9e9e`,
                                  },
                                  "&.Mui-focused fieldset": {
                                    border: `1.5px solid ${T.primary}`,
                                    boxShadow: `0 0 0 2px ${T.primaryLight}`,
                                  },
                                  "&.Mui-disabled": {
                                    bgcolor: "#f0f0f0",
                                  },
                                  "&.Mui-disabled fieldset": {
                                    border: `1.5px solid #bdbdbd`,
                                  },
                                },
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#555",
                                  cursor: "not-allowed",
                                  fontWeight: 600,
                                  opacity: 1,
                                },
                                "& .MuiInputBase-input": {
                                  color: selectedFreightIds.includes(
                                    row.freight_id,
                                  )
                                    ? T.primary
                                    : "#555",
                                  fontWeight: 700,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>{formatDate(row.loading_date)}</TableCell>
                          <TableCell>
                            {formatDate(row.unloading_date)}
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.from_location}
                              maxWidth={90}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.to_location}
                              maxWidth={90}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.truck_owner}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>{row.phone_no || "—"}</TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...stickyCell,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEditOpen(row)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={(e) => {
                                  setDeleteAnchorEl(e.currentTarget);
                                  setDeleteId(row.freight_id);
                                }}
                              />
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                isPrint
                                onClick={() => handlePrintSingleRow(row)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            background: "#6862e0",
            px: 2.5,
            py: 1.75,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.white,
              fontSize: "0.88rem",
              letterSpacing: "0.01em",
            }}
          >
            Edit LR Record
          </Typography>
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={editRow.is_remember || false}
                onChange={(e) =>
                  setEditRow((prev) => ({
                    ...prev,
                    is_remember: e.target.checked,
                  }))
                }
                sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.75rem" }}>Remember</Typography>
            }
          />
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={3}>
              <TextField
                label="From"
                fullWidth
                size="small"
                value={editRow.from_location || ""}
                onChange={(e) =>
                  handleEditChange("from_location", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="To"
                fullWidth
                size="small"
                value={editRow.to_location || ""}
                onChange={(e) =>
                  handleEditChange("to_location", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Loading Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={editRow.loading_date || ""}
                onChange={(e) =>
                  handleEditChange("loading_date", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Unloading Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={editRow.unloading_date || ""}
                onChange={(e) =>
                  handleEditChange("unloading_date", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Truck No"
                fullWidth
                size="small"
                value={editRow.truck_no || ""}
                onChange={(e) => handleEditChange("truck_no", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Truck Owner"
                fullWidth
                size="small"
                value={editRow.truck_owner || ""}
                onChange={(e) =>
                  handleEditChange("truck_owner", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Phone No."
                fullWidth
                size="small"
                value={editRow.phone_no || ""}
                onChange={(e) => handleEditChange("phone_no", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="PAN No"
                fullWidth
                size="small"
                value={editRow.pan_no || ""}
                onChange={(e) => handleEditChange("pan_no", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="TP No"
                fullWidth
                size="small"
                value={editRow.tp_no || ""}
                onChange={(e) => handleEditChange("tp_no", e.target.value)}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                label="Loading Wt"
                type="number"
                fullWidth
                size="small"
                value={editRow.loading_wt || ""}
                onChange={(e) => handleEditChange("loading_wt", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Receiving Wt"
                type="number"
                fullWidth
                size="small"
                value={editRow.received_wt || ""}
                onChange={(e) =>
                  handleEditChange("received_wt", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="LR Rate"
                type="number"
                fullWidth
                size="small"
                value={editRow.freight_rate || ""}
                onChange={(e) =>
                  handleEditChange("freight_rate", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Shortage Rate"
                type="number"
                fullWidth
                size="small"
                value={editRow.shortage_rate || ""}
                onChange={(e) =>
                  handleEditChange("shortage_rate", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="TDS %"
                type="number"
                fullWidth
                size="small"
                value={editRow.tds_per || ""}
                onChange={(e) => handleEditChange("tds_per", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Commission"
                type="number"
                fullWidth
                size="small"
                value={editRow.commission || ""}
                onChange={(e) => handleEditChange("commission", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Ad Mines"
                type="number"
                fullWidth
                size="small"
                value={editRow.ad_mines_exp || ""}
                onChange={(e) =>
                  handleEditChange("ad_mines_exp", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Ad Diesel"
                type="number"
                fullWidth
                size="small"
                value={editRow.ad_diesel_exp || ""}
                onChange={(e) =>
                  handleEditChange("ad_diesel_exp", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Ad Cash"
                type="number"
                fullWidth
                size="small"
                value={editRow.ad_cash_exp || ""}
                onChange={(e) =>
                  handleEditChange("ad_cash_exp", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Others Exp."
                type="number"
                fullWidth
                size="small"
                value={editRow.other_exp || ""}
                onChange={(e) => handleEditChange("other_exp", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={editRow.remark || ""}
                onChange={(e) => handleEditChange("remark", e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setEditDialogOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleEditSave}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            ...sq,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            border: `1.5px solid ${T.border}`,
            bgcolor: T.white,
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 240 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                ...sq,
                bgcolor: T.redBg,
                border: `1.5px solid ${T.redBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
            </Box>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            Delete LR Record?
          </Typography>
          <Typography
            sx={{
              fontSize: "0.67rem",
              color: T.textSub,
              fontWeight: 500,
              textAlign: "center",
              mb: 1.75,
            }}
          >
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setDeleteAnchorEl(null);
                setDeleteId(null);
              }}
              sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              size="small"
              onClick={() => handleDelete(deleteId)}
              sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            ...sq,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "0.82rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
